/* eslint-disable camelcase */
// @flow

import type { Store } from 'redux';
import { isLiveLinearSourceType } from 'Common/models/franchise';
import { TRANSITIONS } from 'Browser/cast/receiver/LiveLinearPlaybackManager';
import type { State, BaseAction, ThunkAction, Dispatch } from 'Common/types';
import type { Cast } from 'Common/cast/types';
import { AD_LOAD_CONST, ADS_DELIVERY_SERVICE } from './AnalyticsConstants';
import BaseCastAnalytics from './BaseCastAnalytics';

const TAG = 'ADOBE[CAST]';

declare var ADBMobile: Object;
declare var cast: Cast;

class AdobeCastAnalytics extends BaseCastAnalytics {
  adBreakPosition: number;
  adPosition: number;

  constructor(playerManager: Object, store: Store<State, BaseAction | ThunkAction, Dispatch>) {
    super(playerManager, store);
    this.adBreakPosition = 0;
    this.adPosition = 0;

    // Enable logging
    ADBMobile.config.setDebugLogging(false); // disabled in the production environment
    // Set media delegate
    ADBMobile.media.setDelegate(this);
  }

  // Media Heartbeat Delegate Methods
  // >>>>> Start

  getCurrentPlaybackTime = () => {
    const time = this.currentPosition;
    this.logAnalytics(TAG, 'Media delegate: current position', time);
    return time;
  };

  getQoSObject = () => {
    const qosInfo = this._qosInfo;
    this.logAnalytics(TAG, 'Media delegate: QoS information', qosInfo);
    return qosInfo;
  };

  destroy = () => ADBMobile.media.setDelegate(null);

  // <<<<< End
  // Media Heartbeat Delegate Methods

  get enabled() {
    return this.customAttributes?.omniture_enabled;
  }

  get _qosInfo() {
    return ADBMobile.media.createQoSObject(
      this.streamBandwidth,
      this.totalDroppedFrames,
      this.encodedFramerate,
      this.currentPosition,
    );
  }

  get _streamType() {
    return ADBMobile.media.MediaType.Video;
  }

  get _mediaInfo() {
    const mediaInfo = ADBMobile.media.createMediaObject(
      this.assetName,
      this.contentId,
      this.contentLength,
      this.sourceType,
      this._streamType,
    );
    mediaInfo[
      ADBMobile.media.MediaObjectKey.StandardMediaMetadata
    ] = this._getStandardMediaMetadata();
    return mediaInfo;
  }

  _initialize() {
    if (this.isInitialized) return;
    // Set User Id
    ADBMobile.config.setUserIdentifier(this.viewerId);

    const mediaMetadata = {
      uuid: this.viewerId,
      channel: this.channelOrNetworkName,
    };

    ADBMobile.media.trackSessionStart(this._mediaInfo, mediaMetadata);
    this.isInitialized = true;
  }

  release() {
    if (!this.isInitialized) return;

    this.logAnalytics(TAG, ' >>>>>  ', 'release');

    ADBMobile.media.trackComplete();
    ADBMobile.media.trackSessionEnd();

    this.isInitialized = false;
    this.adBreakPosition = 0;
    this.adPosition = 0;
    this.isLlAdPlaying = false;
  }

  onCafEvent(event: Object) {
    this.logAnalytics(TAG, 'Player event: ', event.type, event);

    switch (event.type) {
      case 'REQUEST_LOAD': {
        const { requestData } = event;
        if (this.isLiveLinearAdStart(requestData)) {
          this._onBreakStart(requestData);
          this._onAdClipStart(requestData);
        } else if (this.isLiveLinearAdProceedPlaying(requestData)) {
          this._onAdClipComplete();
          this._onAdClipStart(requestData);
        } else if (this.isLiveLinearAdEnd(requestData)) {
          this._onAdClipComplete();
          this._onAdBreakComplete();
        }
        event.handleInSuper = true;
        break;
      }
      case 'PLAYER_LOADING': {
        if (isLiveLinearSourceType(this.sourceType)) {
          this.checkQvtAndStart(() => {
            this._initialize();
          });
        } else {
          this._initialize();
        }
        break;
      }

      case 'REQUEST_PLAY': {
        this._onPlay();
        break;
      }
      case 'REQUEST_PAUSE': {
        this._onPause();
        break;
      }
      case 'REQUEST_SEEK': {
        this._onSeekStart();
        break;
      }
      case 'SEEKED': {
        this._onSeekComplete();
        break;
      }
      case 'BUFFERING': {
        if (event.isBuffering) {
          this._onBufferStart();
        } else {
          this._onBufferComplete();
        }
        break;
      }
      case 'CLIP_ENDED': {
        this.release();
        break;
      }
      case 'BREAK_STARTED':
        this._onBreakStart(event);
        break;
      case 'BREAK_ENDED':
        this._onAdBreakComplete();
        break;
      case 'BREAK_CLIP_STARTED': {
        this._onAdClipStart(event);
        break;
      }
      case 'BREAK_CLIP_ENDED':
        this._onAdClipComplete();
        break;
      default:
        event.handleInSuper = true;
        break;
    }
    return event;
  }

  onCafShutdown() {
    this.release();
  }

  _onPlay() {
    this.logAnalytics(TAG, ' >>>>> ', 'Track PLAY event');
    ADBMobile.media.trackPlay();
  }

  _onPause() {
    this.logAnalytics(TAG, ' >>>>> ', 'Track PAUSE event');
    ADBMobile.media.trackPause();
  }

  _onSeekStart() {
    this.logAnalytics(TAG, ' >>>>> ', 'Track SEEK START event');
    ADBMobile.media.trackEvent(ADBMobile.media.Event.SeekStart);
  }

  _onSeekComplete() {
    this.logAnalytics(TAG, ' >>>>> ', 'Track SEEK COMPLETE event');
    ADBMobile.media.trackEvent(ADBMobile.media.Event.SeekComplete);
  }

  _onBufferStart() {
    this.logAnalytics(TAG, ' >>>>> ', 'Track BUFFER START event');
    ADBMobile.media.trackEvent(ADBMobile.media.Event.BufferStart);
  }

  _onBufferComplete() {
    this.logAnalytics(TAG, ' >>>>> ', 'Track BUFFER COMPLETE event');
    ADBMobile.media.trackEvent(ADBMobile.media.Event.BufferComplete);
  }

  _onBreakStart(event: Object) {
    this.adBreakPosition++;
    const adBreak = this._getAdBreakInfo(event);

    const adBreakInfo = ADBMobile.media.createAdBreakObject(
      adBreak.name,
      adBreak.position,
      adBreak.startTime,
    );
    this.logAnalytics(TAG, ' >>>>> ', 'Track AD BREAK START event');
    ADBMobile.media.trackEvent(ADBMobile.media.Event.AdBreakStart, adBreakInfo);
  }

  _onAdBreakComplete() {
    this.logAnalytics(TAG, ' >>>>> ', 'Track AD BREAK COMPLETE event');
    ADBMobile.media.trackEvent(ADBMobile.media.Event.AdBreakComplete);
  }

  _onAdClipStart(ad: Object) {
    this.adPosition++;

    const adInfo = this._getAdInfo(ad);
    const adClip = ADBMobile.media.createAdObject(
      adInfo.name,
      adInfo.id,
      adInfo.position,
      adInfo.length,
    );

    const standardAdMetadata = {};
    standardAdMetadata[ADBMobile.media.AdMetadataKeys.ADVERTISER] = ADS_DELIVERY_SERVICE;

    this.logAnalytics(TAG, ' >>>>> ', 'Track AD CLIP START event');
    ADBMobile.media.trackEvent(ADBMobile.media.Event.AdStart, adClip, standardAdMetadata);
  }

  _onAdClipComplete() {
    this.logAnalytics(TAG, ' >>>>> ', 'Track AD CLIP COMPLETE event');
    ADBMobile.media.trackEvent(ADBMobile.media.Event.AdComplete);
  }

  _getAdBreakTitle(adBreakId: string) {
    const currentAdBreak = this.breakManager.getBreakClipById(adBreakId);
    return currentAdBreak?.title || 'N/A';
  }

  _getAdInfo(ad) {
    return {
      name: isLiveLinearSourceType(this.sourceType)
        ? this.getCurrentLiveLinearAdInfo(ad.media?.contentUrl)?.title || 'N/A'
        : this.breakManager.getBreakClipById(ad.breakClipId)?.title || 'N/A',
      id: ad?.breakClipId || this.adPosition.toString(),
      position: this.adPosition,
      length: isLiveLinearSourceType(this.sourceType)
        ? this.getCurrentLiveLinearAdInfo(ad.media?.contentUrl)?.duration || 0
        : this.breakManager.getBreakClipDurationSec() || 0,
    };
  }

  _getAdBreakInfo(event: Object) {
    return {
      name: isLiveLinearSourceType(this.sourceType)
        ? this.assetName
        : this._getAdBreakTitle(event?.breakId),
      position: this.adBreakPosition,
      startTime: this.currentPosition,
    };
  }

  _getStandardMediaMetadata() {
    const metadata = {};
    metadata[ADBMobile.media.VideoMetadataKeys.SHOW] = this.assetName;
    metadata[ADBMobile.media.VideoMetadataKeys.SEASON] = this.seasonNumber;
    metadata[ADBMobile.media.VideoMetadataKeys.EPISODE] = this.episodeTitle;
    metadata[ADBMobile.media.VideoMetadataKeys.NETWORK] = this.channelOrNetworkName;
    metadata[ADBMobile.media.VideoMetadataKeys.GENRE] = this.contentGenresString;
    metadata[ADBMobile.media.VideoMetadataKeys.ASSET_ID] = this.contentId;
    metadata[ADBMobile.media.VideoMetadataKeys.FIRST_AIR_DATE] = this.releaseDate;
    metadata[ADBMobile.media.VideoMetadataKeys.STREAM_FORMAT] = this.contentType;
    metadata[ADBMobile.media.VideoMetadataKeys.RATING] = this.rating;
    metadata[ADBMobile.media.VideoMetadataKeys.SHOW_TYPE] = this.kind;
    metadata[ADBMobile.media.VideoMetadataKeys.AD_LOAD] = AD_LOAD_CONST;

    return metadata;
  }

  onPlaybackFinished() {
    if (!this.isLlAdPlaying) {
      this.release();
    }
  }
}

export default AdobeCastAnalytics;
